// src/components/homepage/CravingBox.jsx
//
// Ticket 21 (The craving box). The reader-facing front door to the
// matchmaker: describe what you're in the mood for, by voice or by typing,
// and it becomes a search. This component owns everything up through a
// successful embed; what happens with that embedding (running match_books,
// rendering a deck) is ticket 23's job ("Rebuild the homepage around the
// craving") -- not built yet, so onSubmit here is a handoff point, not a
// finished feature.
//
// DESIGN: reskinned against a reference mockup Robert shared
// (ReaderHomepageSpecBuild's "back cover composer"), scoped to ONLY the
// craving box per his explicit instruction -- nothing about the rest of
// that mockup (the match deck, the pipeline, the sticky dock) touched this
// file or ReaderHomepage.jsx's layout. Three things in that mockup were
// deliberately left out because they sit outside ticket 21's own bullets:
//   - the free-text "add your own chip" search box (chips here are
//     paste-only, exactly as ticket 21 specifies)
//   - the "Overheard on the Shelf" marquee (a real, separately-speced
//     feature -- not part of this ticket; built in ticket 25 as
//     OverheardRows.jsx, which loads a tapped row into this box through the
//     `seed` prop)
//   - the "printed spice" rating selector (nothing downstream reads a
//     spice preference yet; adding the control with no consumer is just
//     decoration wearing a knob)
// One thing WAS added back in past ticket 21's own bullets: the "(i)" info
// tooltip linking to /how-matching-works, confirmed explicitly with Robert
// even though ticket 21 doesn't mention it.
//
// STATE MACHINE
//   idle        -- empty box: rotating examples, trope/genre chips, mic
//                  button, "prefer to type" link always visible next to it.
//   typing      -- reader is typing (or edited a chip-pasted string) --
//                  no confirm step for this path, there's nothing ASR
//                  could have mangled.
//   recording   -- mic live, waveform, tap to stop or cancel.
//   transcribing -- waiting on voice-to-text.
//   confirming  -- VOICE PATH ONLY. "Here's what I heard" -- an editable,
//                  pre-filled transcript. Nothing is embedded or queried
//                  until the reader confirms it here (ticket 21's own
//                  words). Typing has no equivalent step: the reader wrote
//                  it themselves, there's nothing to mishear.
//   embedding   -- confirmed text is being turned into a vector
//                  (embedCraving, ticket 20).
//   error       -- the embed failed (timeout or otherwise). Recoverable:
//                  the text is kept, "Try again" re-embeds the same text
//                  rather than discarding it.
//
// AUDIO: CRAVING AUDIO IS DISCARDED, JOURNAL AUDIO IS STORED, ON PURPOSE.
// useVoiceRecorder() is called below with only a timeout override. Its
// default (keepAudio: false) is exactly what this box needs. The journal's
// audio-keeping (ticket 6/6b) is an explicit opt-in a caller has to request
// by name (`keepAudio: true`); this file never does, so there is no path
// here that can accidentally inherit that retention behaviour. Do not add
// `keepAudio` to the options below.
//
// GUEST GATE: `gate` is a prop, not a fresh useGuestGate() call, on
// purpose -- it has to be the SAME instance ReaderHomepage.jsx already
// passes to <SignUpGate gate={gate} />, or calling gate.trackCravingSubmit()
// here would update a disconnected copy of the gate state and the sign-up
// modal would never actually show.
//
// LATENCY, FLAGGED HONESTLY RATHER THAN PAPERED OVER: the research doc's
// original craving-box budget was transcribe 4s + embed 2s + query 1.5s
// inside an 8s outer ceiling. Ticket 20's own review already found the
// embed leg alone needs up to 7000ms outer to succeed reliably in
// production -- close to the ENTIRE original 8s budget on its own. This
// file passes transcribeTimeoutMs matching the research doc's original 4s
// plan, but that number has NOT been measured against real Google
// speech-to-text latency the way ticket 20's embed number was measured
// against real OpenAI latency -- it's a placeholder with the same shape of
// risk ticket 20's had before it was tested. The 8s-total ceiling as a
// single enforced deadline is NOT implemented here (nothing in ticket 21's
// own bullets asks for one) -- each leg has its own honest inner timeout
// instead, and the total-budget question is left for whoever wires the
// full flow together to revisit with real numbers.
//
// MINIMUM LENGTH: word count, not character count, and not a hard block.
// The 20-word figure comes from the design reference's own readiness copy
// -- ticket 21 asks for "a gentle nudge" with no number attached, so this
// is a borrowed number, not a measured one, worth confirming with whoever
// owns Reanlo's product feel. The submit control stays clickable below
// that count; it just doesn't fire the embed yet and shows a warm nudge
// instead, per ticket 21's own words: "not a hard block with a red error."
//
// COPY IS DRAFT. HEADLINES and the fixed subtext below exist to prove the
// rotation and the "not romance-only" requirement work -- they are not
// blessed marketing copy. (The example cravings were a hardcoded list here
// until ticket 25; they now come from public.overheard_items.) Ticket 21
// gives two phrasings verbatim: "however you'd describe it to a friend" (now the fixed subtext
// under the rotating headline, following the design reference's own
// layout) and "find the one" (the submit button label). The four rotating
// headlines are this file's own placeholders.
// Flag to whoever owns Reanlo's voice before this ships.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { useTagVocabulary } from '../../hooks/useTagVocabulary';
import { embedCraving } from '../../lib/bookSearch';
import { cravingTimingStart, cravingMark, cravingTimingEnd } from '../../lib/craveTiming';
import { supabase, SUPABASE_PUBLISHABLE_KEY } from '../../lib/supabase';
import { MIN_CRAVING_WORDS, wordsOf } from '../../lib/cravingWords';
import { queryDeadline } from '../../lib/queryDeadline';

// Matches the research doc's original, UNMEASURED plan for this leg (see
// the file-header latency note). useVoiceRecorder's own default
// (DEFAULT_TRANSCRIBE_TIMEOUT_MS, 20s) is sized for the journal screen,
// where a reader is not mid-search -- taking that default here would be
// exactly the "inner timeout outlives what the browser will wait for" trap
// ticket 20 already found and fixed once for the embed leg.
// Real number, not the original 4000ms placeholder that turned out to
// undercount reality (a live test hit "TimeoutError: Signal timed out" in
// the voice-to-text function logs at 4000ms -- same shape of mistake
// ticket 20 already made once for the embed leg). Root cause turned out to
// be upload size, not raw transcription cost: useVoiceRecorder.js was
// recording at the browser's native hardware rate (44.1/48kHz) and
// uploading that uncompressed, when Google's speech:recognize only wants
// 16kHz and silently downsamples anything higher on its own end anyway --
// fixed separately in useVoiceRecorder.js (see TRANSCRIBE_SAMPLE_RATE_HZ
// there). After that fix, two live tests at the new 16kHz rate came back
// in 4.32s and 4.2s -- one of them for a LONGER (15.1s) recording than the
// pre-fix ~10s tests that needed 7-8s. 8000ms leaves ~2x margin over both
// measured samples, same proportion ticket 20 used over its own measured
// numbers. Re-measure if real usage ever shows this clipping.
const CRAVING_TRANSCRIBE_TIMEOUT_MS = 8000;

// Ticket 42: the craving save used to have no deadline of its own. The match
// no longer waits for it (see submit()), so this only bounds the bookkeeping,
// never the spinner. 10 s, above the database's own
// cutoff for a signed-in reader (8 s, src/lib/queryDeadline.js), so a slow
// query ends at the database and only a stalled connection ends here. It
// measured 0.7 to 1.1 s. A save that gives up here is not a failed craving:
// recordCraving already returns null on any failure and the page carries on
// without the row's id, which only means the score is not written back.
const CRAVING_SAVE_TIMEOUT_MS = 10000;

// [prefix, accent, suffix] -- the middle segment renders in rust, matching
// the design reference. Kept genre-agnostic on purpose (ticket 21: "so it
// never reads as romance-only") -- none of these four assume romance,
// thriller, or any single genre.
const HEADLINES = [
  ["Write the back cover of ", "the book you're craving", "."],
  ["Describe the story you ", "can't stop thinking about", "."],
  ["Tell us the book that's ", "missing from your shelf", "."],
  ["Somewhere out there is ", "exactly what you're craving", " -- describe it."],
];
const HEADLINE_SUBTEXT = "However you'd describe it to a friend.";
const SUBMIT_LABEL = 'find the one';

// Shown in the box while it is empty when there are no Overheard examples to
// rotate (still loading, or the read failed). The rotating examples themselves
// come from public.overheard_items via the `examples` prop (ticket 25): one list
// that the homepage rows and these placeholders share, every item at least
// MIN_CRAVING_WORDS long. Nothing is hardcoded here, so there is no second list
// to drift from the first.
const FALLBACK_PLACEHOLDER = 'Describe the book you wish existed…';

const ROTATE_MS = 4000;
const MAX_CHIPS = 8;

function useRotator(length, intervalMs) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * length));
  useEffect(() => {
    if (length < 2) return undefined;   // nothing to rotate (and % 0 is NaN)
    const id = setInterval(() => setIndex(i => (i + 1) % length), intervalMs);
    return () => clearInterval(id);
  }, [length, intervalMs]);
  return length > 0 ? index % length : 0;
}

// Tag names are plain words/spaces today, but escaping before it isn't
// true keeps this correct rather than merely working -- a name with a
// regex-special character would otherwise change what the pattern means
// instead of just what it matches.
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Ticket 16b (actually record the craving). public.cravings has exactly one
// grant -- insert, to anon and authenticated (20260913000000_cravings_table
// .sql) -- so this writes the row once, complete, after embedCraving() has
// already come back; there is no update path to add the vector later.
//
// Called on every submit, including a failed embed: a craving that timed
// out is still a real thing a reader asked for (the text is the history,
// the vector is a bonus). embedding/sourceHash are both null together or
// both populated together -- never one without the other, so a failed
// embed never reads as up to date (the migration's own words).
//
// Never throws and never surfaces anything to the reader. submit() does not
// wait for it either (ticket 42): it starts this, hands onSubmit the promise
// and the match starts at once; only the score write-back needs the id and
// waits for it. The insert is bookkeeping. A failure is destructured and sent to Sentry
// instead -- scripts/check-silent-writes.mjs's BASELINE (70) must not move
// for this, and a `const { error } = await supabase...` is exactly the
// shape it recognises as checked.
//
// Ticket 27: now returns the row's own id (or null on any failure), so a
// caller can write best_match_similarity back onto this exact row once
// match_books returns -- 20260930000000_ticket27_honest_empty_state.sql's
// PART 3 is what makes reading it back here possible at all (cravings had
// no SELECT grant before that migration). Still never throws, and the promise
// this returns always resolves (to the id, or null) and never rejects, so it
// can be handed on without a catch: a null id just means the later write-back
// quietly has nothing to target, same as any other bookkeeping failure here.
async function recordCraving(text, session, embedding, sourceHash) {
  const deadline = queryDeadline(CRAVING_SAVE_TIMEOUT_MS);
  try {
    const { data, error } = await supabase.from('cravings').insert({
      // Same session already read for embedCraving() a few lines up in
      // submit() -- reused, not re-fetched. Signed-out is reader_id: null,
      // which the table's own insert policy allows.
      reader_id: session?.user?.id ?? null,
      craving_text: text,
      embedding,
      embedding_source_hash: sourceHash,
    }).select('id').single().abortSignal(deadline.signal);
    if (error) { Sentry.captureException(error); return null; }
    return data?.id ?? null;
  } catch (err) {
    Sentry.captureException(err);
    return null;
  } finally {
    deadline.clear();
  }
}

// Circular mic trigger -- ember + glow rings while live, forest at rest.
// Same visual language as the design reference's Mic component, ported to
// real tokens instead of its hardcoded hex.
function MicButton({ active, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={active ? 'stop recording' : 'speak your craving'}
      style={{
        width: 56,
        height: 56,
        borderRadius: 'var(--radius-circle)',
        border: 'none',
        flexShrink: 0,
        cursor: disabled ? 'default' : 'pointer',
        background: active ? '#e8603c' : 'var(--brand-forest)',
        color: 'var(--brand-cream)',
        fontSize: 20,
        boxShadow: active
          ? '0 0 0 10px rgba(232,96,60,0.14), 0 0 0 20px rgba(232,96,60,0.07)'
          : '4px 5px 0 rgba(45,74,62,0.25)',
        transition: 'all .25s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {active ? '…' : '🎙'}
    </button>
  );
}

// "(i)" reassurance tooltip -- not part of ticket 21's own bullets, added
// back in on Robert's explicit say-so. Copy matches the design reference.
function InfoTip() {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        aria-label="how matching works"
        onClick={() => setOpen(o => !o)}
        style={{
          width: 22,
          height: 22,
          borderRadius: 'var(--radius-circle)',
          border: '1.5px solid var(--neutral-300)',
          background: 'transparent',
          color: 'var(--neutral-400)',
          fontSize: 11,
          fontStyle: 'italic',
          fontFamily: 'var(--font-display)',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >i</button>
      {open && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 30,
            left: -12,
            width: 260,
            zIndex: 20,
            background: 'var(--brand-white)',
            border: '1.5px solid var(--neutral-200)',
            borderRadius: 'var(--radius-lg)',
            padding: '12px 14px 10px',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="dismiss"
            style={{
              position: 'absolute',
              top: 6,
              right: 8,
              background: 'none',
              border: 'none',
              color: 'var(--neutral-400)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >✕</button>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--neutral-800)',
              lineHeight: 1.55,
              paddingRight: 12,
              fontFamily: 'var(--font-body)',
            }}
          >
            <strong>Not AI.</strong> Nothing here writes or summarizes your words. When you describe
            the book you're craving, we match the meaning of what you wrote to how authors describe
            their own books. Human words to human words.
          </div>
          <a
            href="/how-matching-works"
            style={{
              display: 'inline-block',
              marginTop: 6,
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              color: 'var(--brand-rust)',
              textDecoration: 'none',
              fontFamily: 'var(--font-heading)',
            }}
          >how matching works →</a>
        </div>
      )}
    </span>
  );
}

// examples: the Overheard texts, already in the order to show them (the page
//   draws a fresh order on every visit). Placeholder text only.
// seed:     { text, key } -- a new key loads `text` into the box, as when a
//   reader taps an Overheard row. The key (not the text) is what triggers it,
//   so tapping the same row twice loads it twice.
export default function CravingBox({ gate, onSubmit, examples = [], seed = null }) {
  const [mode, setMode] = useState('idle'); // idle | typing | recording | transcribing | confirming | embedding | error
  const [text, setText] = useState('');
  const [nudge, setNudge] = useState(false);
  const [errorReason, setErrorReason] = useState(null); // 'timeout' | 'error'
  const textareaRef = useRef(null);

  const headlineIndex = useRotator(HEADLINES.length, ROTATE_MS);
  const exampleIndex  = useRotator(examples.length, ROTATE_MS);
  // loading/error are read, not dropped: an empty vocab.trope/genre means
  // two different things -- "the vocabulary is genuinely empty" and "the
  // read failed" -- and useTagVocabulary's own docblock is explicit that
  // returning eight empty arrays and letting the reader conclude Reanlo has
  // no tropes is the one thing this hook must never do. FilterSheet.jsx
  // already destructures all three for the same reason; this box was the
  // one place that didn't.
  const { vocab, loading: vocabLoading, error: vocabError } = useTagVocabulary();
  const tropeChips = (vocab.trope || []).slice(0, MAX_CHIPS);
  const genreChips = (vocab.genre || []).slice(0, MAX_CHIPS);

  const recorder = useVoiceRecorder({ transcribeTimeoutMs: CRAVING_TRANSCRIBE_TIMEOUT_MS });

  // Keep this box's mode in step with the recorder's own phase rather than
  // tracking a second, parallel copy of "are we recording" -- two sources
  // of truth for the same fact is how they drift.
  useEffect(() => {
    if (recorder.state === 'recording') setMode('recording');
    else if (recorder.state === 'transcribing') setMode('transcribing');
    else if (recorder.state === 'error') {
      setMode('idle');
      // recorder.error already carries a reader-facing message (e.g.
      // "Transcription failed. You can type instead.") -- surfaced via the
      // nudge slot rather than a second error UI.
    }
  }, [recorder.state]);

  const startRecording = useCallback(() => {
    setNudge(false);
    recorder.start();
  }, [recorder]);

  const stopRecording = useCallback(async () => {
    const transcript = await recorder.stop();
    if (transcript) {
      setText(transcript);
      setMode('confirming');
    } else {
      // Empty/failed transcription -- recorder.error is set, land back on
      // idle rather than a confirm screen with nothing to confirm.
      setMode('idle');
    }
  }, [recorder]);

  const switchToTyping = useCallback(() => {
    recorder.cancel();
    setMode('typing');
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [recorder]);

  const pasteChip = useCallback((chip) => {
    setText(prev => {
      const trimmed = prev.trim();
      if (!trimmed) return chip;
      // Comma-separated rather than appended as a sentence -- chips are
      // fragments a reader is assembling, not a script they're speaking.
      return `${trimmed}, ${chip}`;
    });
    setMode(m => (m === 'confirming' ? m : 'typing'));
    setNudge(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  // A tapped Overheard row. Ignored while the box is busy (recording,
  // transcribing, embedding): loading text under a live recording or an embed in
  // flight would swap the words out from under the reader. Placed in the box as
  // typed text, so it is theirs to edit before "find the one".
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const seedKey = seed?.key;
  const seedText = seed?.text;
  useEffect(() => {
    if (seedKey == null || !seedText) return;
    if (['recording', 'transcribing', 'embedding'].includes(modeRef.current)) return;
    setText(seedText);
    setMode('typing');
    setNudge(false);
    setErrorReason(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [seedKey, seedText]);

  const submit = useCallback(async (finalText) => {
    const trimmed = (finalText ?? '').trim();
    if (wordsOf(trimmed) < MIN_CRAVING_WORDS) {
      setNudge(true);
      return;
    }
    setNudge(false);
    setErrorReason(null);

    // Counts on submission itself, not on whether the embed comes back ok
    // -- a guest who used the feature and hit a timeout still used it.
    gate?.trackCravingSubmit?.();

    // Ticket 31: stopwatch, off unless the page address has ?timing=1.
    cravingTimingStart('typed');
    setMode('embedding');
    const { data: { session } } = await supabase.auth.getSession();
    cravingMark('session-ready');
    cravingMark('embed-start');
    const result = await embedCraving(trimmed, session, SUPABASE_PUBLISHABLE_KEY);
    cravingMark('embed-end', result.status);

    if (result.status === 'ok') {
      // Ticket 42: the save and the match do not depend on each other.
      // match_books never uses the craving's id; only the score write-back
      // after it does. So the save is STARTED here (its request goes out
      // before onSubmit runs, as in ticket 16b) but not waited for: the match
      // starts straight away, and the page gets the save's promise to wait on
      // only where it needs the id. recordCraving() never throws and its
      // promise never rejects, so there is nothing to catch here.
      cravingMark('record-start');
      const cravingIdPromise = recordCraving(trimmed, session, result.embedding, result.sourceHash);
      cravingIdPromise.then(() => cravingMark('record-end'));
      onSubmit?.(trimmed, result.embedding, cravingIdPromise);
      setMode('idle');
      setText('');
    } else {
      // Still recorded -- a timed-out or failed embed is a real craving a
      // reader asked for (ticket 16b). embedding/sourceHash both null: a
      // failed embed must never read as up to date. Not waited for (ticket
      // 42): the reader sees the error at once, and the save finishes or
      // gives up on its own. Not marked on the stopwatch either, since the
      // report below prints before it could land.
      recordCraving(trimmed, session, null, null);
      cravingTimingEnd(`embed ${result.status}`);
      setErrorReason(result.status); // 'timeout' | 'error'
      setMode('error');
    }
  }, [gate, onSubmit]);

  // ── styles, matching the hero section's existing token conventions ──────
  const outerCard = {
    background: 'var(--brand-white)',
    borderRadius: 'var(--radius-xxl)',
    border: '1px solid rgba(184,92,56,0.15)',
    boxShadow: '4px 4px 0 rgba(184,92,56,0.06)',
    padding: 'var(--space-xxl)',
    marginBottom: 'var(--space-xxl)',
  };
  const headlineStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--font-size-h4)',
    fontWeight: 700,
    color: 'var(--neutral-800)',
    margin: 0,
  };
  const subtextStyle = {
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--neutral-500)',
    marginTop: 'var(--space-sm)',
  };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 'var(--space-xxl)',
    alignItems: 'start',
  };
  const composerStyle = {
    borderRadius: '6px 20px 20px 6px',
    background: 'linear-gradient(165deg, var(--brand-forest) 0%, var(--neutral-950) 140%)',
    color: 'var(--brand-cream)',
    boxShadow: 'inset 8px 0 10px -6px rgba(0,0,0,0.5), var(--shadow-lg)',
    padding: 'var(--space-xl) var(--space-lg)',
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
  };
  const composerLabelStyle = {
    fontFamily: 'var(--font-heading)',
    fontSize: 'var(--font-size-tiny)',
    letterSpacing: '0.22em',
    color: 'var(--brand-gold)',
    fontWeight: 700,
  };
  const textareaComposerStyle = {
    flex: 1,
    width: '100%',
    marginTop: 'var(--space-md)',
    border: 'none',
    outline: 'none',
    resize: 'none',
    background: 'transparent',
    color: 'var(--brand-cream)',
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--font-size-lg)',
    lineHeight: 1.7,
    fontStyle: 'italic',
    boxSizing: 'border-box',
  };
  const stampStyle = {
    fontSize: 'var(--font-size-tiny)',
    letterSpacing: '0.06em',
    border: '1px solid var(--brand-gold)',
    color: 'var(--brand-gold)',
    borderRadius: 'var(--radius-sm)',
    padding: '2px 7px',
    textTransform: 'uppercase',
  };
  const confirmBannerStyle = {
    marginTop: 'var(--space-md)',
    background: 'rgba(212,168,75,0.16)',
    border: '1px solid rgba(212,168,75,0.4)',
    borderRadius: 'var(--radius-md)',
    padding: '8px 12px',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--brand-gold-light)',
  };
  const hintStyle = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--neutral-500)',
  };
  const nudgeStyle = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--brand-rust)',
    fontWeight: 600,
  };
  const ghostBtn = {
    background: 'transparent',
    color: 'var(--brand-rust)',
    border: 'none',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  };
  const primaryBtn = {
    background: 'var(--brand-rust)',
    color: 'var(--brand-white)',
    border: 'none',
    borderRadius: 'var(--radius-xxl)',
    padding: '12px 26px',
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
    fontSize: 'var(--font-size-md)',
    cursor: 'pointer',
    boxShadow: '3px 4px 0 rgba(184,92,56,0.25)',
  };
  const chipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', alignItems: 'center' };
  const chipLabelStyle = {
    fontFamily: 'var(--font-heading)',
    fontSize: 'var(--font-size-tiny)',
    letterSpacing: '0.18em',
    color: 'var(--neutral-400)',
    fontWeight: 700,
    marginRight: 2,
  };
  // fontFamily: 'inherit' because a <button> does not inherit the page
  // font the way a <span> does in every browser that matters here -- these
  // render as real buttons now (see the chip rows below), and without this
  // they'd visually mismatch every other button in the box.
  const tropeChipStyle = {
    padding: '5px 12px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--brand-white)',
    color: 'var(--neutral-800)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'inherit',
    border: '1.5px solid var(--neutral-200)',
    cursor: 'pointer',
  };
  const genreChipStyle = {
    padding: '5px 12px',
    borderRadius: 'var(--radius-pill)',
    background: 'rgba(212,168,75,0.12)',
    color: 'var(--neutral-800)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'inherit',
    border: '1.5px solid rgba(212,168,75,0.5)',
    cursor: 'pointer',
  };

  // ── derived render state ─────────────────────────────────────────────────
  const isTyping = mode === 'typing';
  const isRecording = mode === 'recording';
  const isTranscribing = mode === 'transcribing';
  const isConfirming = mode === 'confirming';
  const isEmbedding = mode === 'embedding';
  const isError = mode === 'error';

  const wordCount = wordsOf(text);
  // Word-boundary match, not substring: a plain .includes() lit up "Horror"
  // inside "horrorshow", "Urban" inside "urbane", "Dark" inside "darkly" --
  // real words that happen to contain a tag name are not the reader
  // invoking that tag. \b on each side anchors the match to whole words (or
  // whole phrases, for a multi-word name like "Dark Academia") rather than
  // any substring position.
  //
  // Longest name first, then drop a shorter match that is itself contained
  // in one already kept -- so "dark academia" in the text stamps "Dark
  // Academia" once rather than also separately stamping the "Dark" that
  // phrase contains.
  const matchedStamps = [...tropeChips, ...genreChips]
    .filter((name) => text && new RegExp(`\\b${escapeRegExp(name)}\\b`, 'i').test(text))
    .sort((a, b) => b.length - a.length)
    .filter((name, i, kept) => !kept.slice(0, i).some((longer) => longer.toLowerCase().includes(name.toLowerCase())));
  const [preH, accentH, postH] = HEADLINES[headlineIndex];

  return (
    <div style={outerCard}>
      <style>{`
        @keyframes cravingFadeSwap { 0%{opacity:0;transform:translateY(6px)} 12%{opacity:1;transform:none} 88%{opacity:1} 100%{opacity:0;transform:translateY(-6px)} }
        @keyframes cravingPulse { from{transform:scaleY(.4)} to{transform:scaleY(1)} }
      `}</style>

      <div style={{ textAlign: 'center', marginBottom: 'var(--space-xxl)' }}>
        <p key={headlineIndex} style={{ ...headlineStyle, animation: 'cravingFadeSwap 4s ease' }}>
          {preH}<em style={{ color: 'var(--brand-rust)', fontStyle: 'normal' }}>{accentH}</em>{postH}
        </p>
        <p style={subtextStyle}>{HEADLINE_SUBTEXT}</p>
      </div>

      <div style={gridStyle}>
        {/* back cover composer */}
        <div style={composerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={composerLabelStyle}>IN YOUR WORDS</span>
            <span style={{ fontSize: 'var(--font-size-tiny)', color: 'rgba(245,240,232,0.5)' }}>back cover</span>
          </div>

          {isRecording && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'var(--space-md)' }}>
              {recorder.waveform.slice(-16).map((v, i) => (
                <div
                  key={i}
                  style={{
                    width: 3,
                    borderRadius: 2,
                    background: 'var(--brand-gold)',
                    height: Math.max(4, v * 22),
                    animation: 'cravingPulse .8s infinite alternate',
                  }}
                />
              ))}
              <span style={{ fontSize: 'var(--font-size-xs)', color: '#e8603c', fontWeight: 700 }}>listening&hellip;</span>
            </div>
          )}

          {isConfirming && (
            <div style={confirmBannerStyle}>
              here's what we heard &mdash; fix anything that's off, then {SUBMIT_LABEL} below
            </div>
          )}

          {isTranscribing ? (
            <p style={{ ...textareaComposerStyle, opacity: 0.6 }}>catching that&hellip;</p>
          ) : (
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => { setText(e.target.value); if (nudge) setNudge(false); }}
              // No onKeyDown submit-on-Enter here. This is a 7-row, 20-word-minimum
              // composer, not a one-line input -- Enter has to keep meaning "new
              // line", which is what a textarea does by default with no handler
              // fighting it. A phone's soft keyboard has no Shift to escape an
              // Enter-submits binding with, so that binding previously made it
              // impossible to write a multi-sentence craving on mobile without
              // an accidental submit mid-thought. The "find the one" button
              // below is the submit control.
              placeholder={isTyping || isConfirming || examples.length === 0 ? FALLBACK_PLACEHOLDER : examples[exampleIndex]}
              rows={7}
              readOnly={isRecording || isEmbedding}
              style={textareaComposerStyle}
            />
          )}

          {matchedStamps.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'var(--space-sm)' }}>
              {matchedStamps.map((c) => (
                <span key={c} style={stampStyle}>{c}</span>
              ))}
            </div>
          )}
        </div>

        {/* controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
            {!isTyping && !isConfirming && (
              <MicButton
                active={isRecording}
                disabled={isTranscribing || isEmbedding}
                onClick={isRecording ? stopRecording : startRecording}
              />
            )}
            <div style={{ flex: 1 }}>
              {isError ? (
                <span style={nudgeStyle}>
                  {errorReason === 'timeout' ? 'That took longer than it should have.' : 'Something went wrong on our end.'}
                  {' '}Your craving is still here, nothing typed was lost.
                </span>
              ) : nudge ? (
                <span style={nudgeStyle}>
                  {wordCount === 0
                    ? `At least ${MIN_CRAVING_WORDS} words gives the matchmaker something to work with.`
                    : `${MIN_CRAVING_WORDS - wordCount} more word${MIN_CRAVING_WORDS - wordCount === 1 ? '' : 's'}: the ache, the vibe, the ending you need.`}
                </span>
              ) : isRecording ? (
                <span style={hintStyle}>tap the mic again when you're done talking</span>
              ) : wordCount >= MIN_CRAVING_WORDS ? (
                <span style={{ ...hintStyle, color: 'var(--brand-forest)', fontWeight: 700 }}>That's a craving. Ready when you are.</span>
              ) : (
                <span style={hintStyle}>Voice by default, or type it out.</span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 2 }}>
                {!isRecording && !isEmbedding && !isConfirming && (
                  <button type="button" style={ghostBtn} onClick={isTyping ? () => setMode('idle') : switchToTyping}>
                    {isTyping ? '🎙 back to voice' : 'prefer to type'}
                  </button>
                )}
                {isRecording && (
                  <button type="button" style={ghostBtn} onClick={() => { recorder.cancel(); setMode('idle'); }}>cancel</button>
                )}
                {isConfirming && (
                  <button type="button" style={ghostBtn} onClick={() => { setText(''); setMode('idle'); }}>start over</button>
                )}
                <InfoTip />
              </div>
            </div>
          </div>

          {recorder.error && <p style={nudgeStyle}>{recorder.error}</p>}

          {/* A failed read used to render as zero chip rows with no
              explanation -- indistinguishable from "Reanlo has no tropes",
              which is exactly what the hook's own docblock says this must
              never look like. Chips are a convenience, not a requirement to
              submit, so this is a quiet inline line rather than an error
              banner. */}
          {vocabError && !vocabLoading && !isRecording && !isEmbedding && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: 0 }}>
              Couldn't load trope and genre suggestions right now, you can still describe your craving in your own words.
            </p>
          )}
          {(tropeChips.length > 0 || genreChips.length > 0) && !isRecording && !isEmbedding && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              {tropeChips.length > 0 && (
                <div style={chipRowStyle}>
                  <span style={chipLabelStyle}>TROPES</span>
                  {/* Real <button>s, not <span onClick>: a span with a click
                      handler is invisible to Tab and to a screen reader. This
                      is the only control in the box that wasn't already one. */}
                  {tropeChips.map((name) => (
                    <button type="button" key={name} style={tropeChipStyle}
                      onClick={() => pasteChip(name)}
                      aria-label={`Add "${name}" to your craving`}>+ {name}</button>
                  ))}
                </div>
              )}
              {genreChips.length > 0 && (
                <div style={chipRowStyle}>
                  <span style={chipLabelStyle}>GENRES</span>
                  {genreChips.map((name) => (
                    <button type="button" key={name} style={genreChipStyle}
                      onClick={() => pasteChip(name)}
                      aria-label={`Add "${name}" to your craving`}>+ {name}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)' }}>
              {wordCount} / {MIN_CRAVING_WORDS} words
            </span>
            {isError ? (
              <button type="button" style={primaryBtn} onClick={() => submit(text)}>try again</button>
            ) : (
              <button
                type="button"
                style={{ ...primaryBtn, opacity: isEmbedding ? 0.7 : 1 }}
                disabled={isEmbedding || isRecording || isTranscribing}
                onClick={() => submit(text)}
              >
                {isEmbedding ? 'finding your match…' : SUBMIT_LABEL}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
